import React from "react";
import {useAuth, UserRole} from "../AuthContext";

interface LoadingSpinnerProps {
  message?: string;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message = "Loading...",
}) => {
  return (
    <div className="env-page-container">
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
          flexDirection: "column",
        }}
      >
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
        <p>{message}</p>
      </div>
    </div>
  );
};

interface AccessDeniedProps {
  title?: string;
  message?: string;
}

const AccessDenied: React.FC<AccessDeniedProps> = ({
  title = "Access Denied",
  message = "You don't have permission to access this page.",
}) => {
  return (
    <div className="env-page-container">
      <h2 className="env-page-title">{title}</h2>
      <div className="env-session-access-denied">
        <div className="env-session-access-denied-title">
          <strong>Access Denied</strong>
        </div>
        <div>{message}</div>
      </div>
    </div>
  );
};

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  requiredAnyRole?: UserRole[];
  loadingMessage?: string;
  denialTitle?: string;
  denialMessage?: string;
  fallbackComponent?: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requiredRoles = [],
  requiredAnyRole = [],
  loadingMessage,
  denialTitle,
  denialMessage,
  fallbackComponent,
}) => {
  const {loading, user, hasRole, hasAnyRole} = useAuth();

  // Show loading while auth resolves
  if (loading) {
    return <LoadingSpinner message={loadingMessage} />;
  }

  // Check if user is authenticated
  if (!user) {
    return (
      <AccessDenied
        title={denialTitle}
        message="You must be logged in to access this page."
      />
    );
  }

  // Check role requirements
  let hasAccess = true;

  // If specific roles are required, user must have ALL of them
  if (requiredRoles.length > 0) {
    hasAccess = requiredRoles.every((role) => hasRole(role));
  }

  // If any roles are specified, user must have at least ONE of them
  if (requiredAnyRole.length > 0) {
    hasAccess = hasAnyRole(requiredAnyRole);
  }

  // Show denial if access requirements not met
  if (!hasAccess) {
    if (fallbackComponent) {
      return <>{fallbackComponent}</>;
    }

    return <AccessDenied title={denialTitle} message={denialMessage} />;
  }

  // User has access, render children
  return <>{children}</>;
};

export default AuthGuard;
export {LoadingSpinner, AccessDenied};
